import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { Fragment, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export default function ComboBox({
  data,
  placeHolder = "Select an option",
  selected,
  setSelected,
  width,
  value,
  defaultValue,
  inputWidth,
  disabled,
  showContactFormatting = false, // New prop to control contact formatting
  isNoContact,
}) {
  const [query, setQuery] = useState("");
  const [loadedData, setLoadedData] = useState([]);
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;
  const listRef = useRef();
  const [isSearching, setIsSearching] = useState(false);

  // Keep latest selected/setSelected in refs so the init effect below only
  // re-runs when the source data itself changes; call sites pass inline
  // arrows for setSelected, and having it in the deps made every parent
  // re-render reset loadedData, wiping any active search results.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const setSelectedRef = useRef(setSelected);
  setSelectedRef.current = setSelected;

  // Pre-process data when it arrives to improve initial render performance
  useEffect(() => {
    if (data?.length > 0) {
      // Initialize with first page of data immediately
      setLoadedData(data.slice(0, itemsPerPage));
      setPage(1);

      // Only auto-select primary item if no selection has been made yet
      if (!selectedRef.current) {
        const primaryItem = data.find((item) => item.primary === true);
        if (primaryItem) {
          setSelectedRef.current(primaryItem);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Lazy loading on scroll
  const handleScroll = () => {
    if (
      listRef.current &&
      listRef.current.scrollTop + listRef.current.clientHeight >=
        listRef.current.scrollHeight - 5 &&
      !isSearching
    ) {
      loadMoreItems();
    }
  };

  const loadMoreItems = () => {
    const nextPage = page + 1;
    const startIndex = page * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    if (data?.length > startIndex) {
      setLoadedData((prevData) => [
        ...prevData,
        ...data.slice(startIndex, endIndex),
      ]);
      setPage(nextPage);
    }
  };

  // Handle search with debounce for better performance
  useEffect(() => {
    if (!query.trim()) {
      setIsSearching(false);
      setLoadedData(data?.slice(0, itemsPerPage) || []);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      const filteredResults =
        data?.filter((item) =>
          `${item.name || ""} ${item.email || ""} ${item.first_name || ""} ${
            item.last_name || ""
          }`
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        ) || [];
      setLoadedData(filteredResults);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [query, data]);

  // Sort data: Primary contacts first (copy first; sorting state in place
  // can suppress re-renders)
  const sortedData = loadedData
    ? [...loadedData].sort((a, b) =>
        a.primary === b.primary ? 0 : a.primary ? -1 : 1
      )
    : [];

  // Function to get formatted label
  const getLabel = (item) => {
    if (!item) return "";

    // Check if this is a contact item by checking for email property
    const isContact = "email" in item;

    // Create prefix for primary/secondary status only for contacts and when showContactFormatting is true
    const prefix =
      isContact && showContactFormatting
        ? item.primary
          ? "(Primary)"
          : "(Secondary)"
        : "";

    // Format name - use first_name and last_name if available
    const name =
      item.first_name || item.last_name
        ? `${item.first_name || ""} ${item.last_name || ""}`.trim()
        : item.name || "";

    // Format the display with name and email for contacts only when showContactFormatting is true
    if (isContact && showContactFormatting) {
      if (name && item.email) {
        return `${prefix} ${name} / ${item.email}`;
      } else if (name) {
        return `${prefix} ${name}`;
      } else if (item.email) {
        return `${prefix} ${item.email}`;
      }
    }

    // For non-contacts or when showContactFormatting is false, just return the name or value
    return item.name || item.label || item.value || "";
  };

  return (
    <div>
      <Combobox value={selected} onChange={setSelected} disabled={disabled}>
        <div className="relative">
          <div
            className={`relative w-full lg:${width} xl:${width} cursor-default overflow-hidden rounded-md bg-white text-left border border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm`}
          >
            <Combobox.Input
              className={`w-full ${
                disabled ? "bg-gray-100" : ""
              } border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0`}
              displayValue={(item) => getLabel(item)}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={placeHolder}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              className={`${
                isNoContact && "hidden"
              } z-50 absolute mt-1 max-h-60 ${inputWidth} overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm`}
              onScroll={handleScroll}
              ref={listRef}
              static
            >
              {sortedData.length === 0 ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  {query !== "" ? "Nothing found." : "Loading..."}
                </div>
              ) : (
                sortedData.map((item) => (
                  <Combobox.Option
                    key={item?.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-5 pr-4 ${
                        active
                          ? "bg-custom-link-bg-color text-white"
                          : "text-gray-900"
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <div className="flex items-center justify-between">
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {getLabel(item)}
                        </span>
                        {selected && (
                          <span
                            className={`flex items-center ${
                              active
                                ? "text-white"
                                : "text-custom-link-bg-color"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
